/**
 * Sharing the current page.
 *
 * `navigator.share` opens the OS share sheet, but it is far from universal — Firefox has it on
 * Android only, and desktop Chrome only on Windows and ChromeOS. So this is written as "try the
 * native sheet, otherwise put the link on the clipboard", and the caller shows which one happened.
 *
 * The capabilities are injected rather than read off `navigator` so the whole thing is testable in
 * jsdom, where neither API exists.
 */

export type ShareOutcome =
  /** The OS share sheet took it. */
  | 'shared'
  /** No share sheet (or it failed) — the URL is on the clipboard instead. */
  | 'copied'
  /** The share sheet opened and the user backed out. Not an error; say nothing. */
  | 'dismissed'
  /** Neither worked. */
  | 'failed';

export interface ShareCaps {
  share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
  copy?: (text: string) => Promise<void>;
}

/** Read the real capabilities off the browser, omitting whatever it does not have. */
export function browserCaps(nav: Navigator = navigator): ShareCaps {
  const caps: ShareCaps = {};
  if (typeof nav.share === 'function') caps.share = (d) => nav.share(d);
  const clip = nav.clipboard;
  if (clip && typeof clip.writeText === 'function') caps.copy = (t) => clip.writeText(t);
  return caps;
}

/**
 * A share-sheet dismissal is reported as a rejected promise, exactly like a real failure. The only
 * thing separating "the user changed their mind" from "this browser is broken" is the error's name,
 * and getting it wrong means every cancelled share falls through to silently rewriting the
 * clipboard — so `AbortError` is checked for explicitly and treated as success-by-another-name.
 */
export async function sharePage(url: string, title: string, caps: ShareCaps): Promise<ShareOutcome> {
  if (caps.share) {
    try {
      await caps.share({ title, url });
      return 'shared';
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      if (name === 'AbortError') return 'dismissed';
      // Anything else — a browser that advertises share and then refuses, a
      // permissions policy, an insecure context — falls through to the clipboard.
    }
  }
  if (caps.copy) {
    try {
      await caps.copy(url);
      return 'copied';
    } catch {
      return 'failed';
    }
  }
  return 'failed';
}
